import type { LocationQuery, LocationQueryValue } from 'vue-router'

export type DateRangeMode = '' | 'minus1' | 'plus1' | 'both'

function queryValue(val: LocationQueryValue | LocationQueryValue[] | null | undefined): string | undefined {
  if (val == null) return undefined
  const raw = Array.isArray(val) ? val[0] : val
  return raw ?? undefined
}

/** Lee `data-dateRangeMode` o parámetros legacy (`data-dateRange`, Before/After). */
export function parseDateRangeModeFromQuery(query: LocationQuery): DateRangeMode {
  const mode = queryValue(query['data-dateRangeMode'])
  if (mode === 'minus1' || mode === 'plus1' || mode === 'both') return mode

  const legacy = Number(queryValue(query['data-dateRange'])) === 1
  const beforeRaw = query['data-dateRangeBefore']
  const afterRaw = query['data-dateRangeAfter']
  const hasBeforeKey = beforeRaw != null
  const hasAfterKey = afterRaw != null
  const before = hasBeforeKey ? Number(queryValue(beforeRaw)) === 1 : legacy
  const after = hasAfterKey ? Number(queryValue(afterRaw)) === 1 : legacy

  if (before && after) return 'both'
  if (before) return 'minus1'
  if (after) return 'plus1'
  return ''
}

export function dateRangeIncludesBefore(mode: DateRangeMode): boolean {
  return mode === 'minus1' || mode === 'both'
}

export function dateRangeIncludesAfter(mode: DateRangeMode): boolean {
  return mode === 'plus1' || mode === 'both'
}

export function hasDateRangeExtra(mode: DateRangeMode): boolean {
  return mode !== ''
}

/** Modo activo: store (UI) o query (URL / recarga). */
export function resolveActiveDateRangeMode(
  storeMode: DateRangeMode,
  query: LocationQuery
): DateRangeMode {
  return storeMode || parseDateRangeModeFromQuery(query)
}

/** Campos de query para persistir el modo en `/search`. */
export function dateRangeModeQueryFields(mode: DateRangeMode): Record<string, string> {
  if (!mode) return {}
  return { 'data-dateRangeMode': mode }
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function addCalendarDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

/** Fechas adyacentes a pedir a `/search` según el modo (p. ej. `plus1` → solo día siguiente). */
export function getAdjacentSearchDatesToFetch(baseDate: Date, mode: DateRangeMode): Date[] {
  if (!hasDateRangeExtra(mode)) return []

  const todayStart = startOfLocalDay(new Date())
  const out: Date[] = []

  if (dateRangeIncludesBefore(mode)) {
    const dBefore = addCalendarDays(baseDate, -1)
    if (startOfLocalDay(dBefore).getTime() >= todayStart.getTime()) out.push(dBefore)
  }
  if (dateRangeIncludesAfter(mode)) {
    out.push(addCalendarDays(baseDate, 1))
  }

  return out
}

/** Filtra la lista fusionada según el modo activo (p. ej. ±1 → solo -1). */
export function filterHotelsByDateRangeMode<T extends { _availableDate?: Date }>(
  hotels: T[],
  mode: DateRangeMode,
  baseDate: Date
): T[] {
  if (!hasDateRangeExtra(mode)) return hotels

  const baseT = startOfLocalDay(baseDate).getTime()
  return hotels.filter((h) => {
    const avail = h._availableDate ?? baseDate
    const availT = startOfLocalDay(avail).getTime()
    if (availT === baseT) return true
    const diffDays = Math.round((availT - baseT) / 86400000)
    if (diffDays === -1 && dateRangeIncludesBefore(mode)) return true
    if (diffDays === 1 && dateRangeIncludesAfter(mode)) return true
    return false
  })
}
